import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "reactstrap";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import CommonPagination from "../../../Common/Pagination/CommonPagination";
import { deleteAllProducts, deleteOneProduct } from "../../../../redux/Product/action";
import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../../lib/permissions";

import DeleteProduct from "./DeleteProduct";
import DeleteAllProducts from "./DeleteAllProducts";
import ExportMenu from "../../../Common/Export/ExportMenu";
import ProductTable from "./ProductTable";
import ProductFiltersBar, { countActiveFilters } from "./ProductFiltersBar";
import { useProductList } from "./hooks/useProductList";
import { useProductFilterOptions } from "./hooks/useProductFilterOptions";
import { useDebouncedValue } from "./hooks/useDebouncedValue";

import "./ProductList.css";

const INITIAL_PARAMS = {
  page: 1,
  search_key: "",
  status: "",
  per_page: "20",
};

const PER_PAGE_OPTIONS = ["20", "50", "100"];

const ProductList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasAction, isSuperAdmin } = usePermissions();
  const canDelete = hasAction(MENU.CATALOGUE, PERMISSION.DELETE);
  // POST /products is gated on EDIT-level cost permission. A VIEW-only manager
  // can see cost but can't create live products with it.
  const canCreateLive = hasAction(MENU.PRODUCT_COSTS, PERMISSION.EDIT);

  // List state (page, filters, search, per_page) lives in the URL so it survives
  // navigating to the edit page and back — see handleEdit + the editor's return.
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const searchKey = searchParams.toString();
  const params = useMemo(() => {
    const p = { ...INITIAL_PARAMS };
    for (const [k, v] of new URLSearchParams(searchKey).entries()) p[k] = v;
    p.page = Number(p.page) || 1;
    return p;
  }, [searchKey]);

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search_key") || ""
  );
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);

  // Write a partial change into the URL query (replace, so paging/filtering
  // doesn't spam browser history). Empty values and defaults (page 1,
  // per_page 20) are dropped to keep the URL clean.
  const patchParams = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            const isDefault =
              v === "" ||
              v == null ||
              (k === "page" && Number(v) === 1) ||
              (k === "per_page" && String(v) === "20");
            if (isDefault) next.delete(k);
            else next.set(k, String(v));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Push the debounced search term into the URL (resets to page 1).
  useEffect(() => {
    const current = new URLSearchParams(searchKey).get("search_key") || "";
    if (current === debouncedSearch) return;
    patchParams({ search_key: debouncedSearch, page: 1 });
  }, [debouncedSearch, searchKey, patchParams]);

  const { items, pagination, loading, refetch } = useProductList(params);
  const filterOptions = useProductFilterOptions(params.category_id);

  const handleEdit = useCallback(
    (item) =>
      navigate(`/updateproduct/${item.id}`, {
        state: { from: location.pathname + location.search },
      }),
    [navigate, location.pathname, location.search]
  );

  const handleDeleteRequest = useCallback((item) => {
    setPendingDelete(item);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (!pendingDelete?.id) return;
    dispatch(deleteOneProduct(pendingDelete.id));
    refetch();
  }, [dispatch, pendingDelete, refetch]);

  const handleDeleteAllConfirm = useCallback(async () => {
    setDeletingAll(true);
    const result = await dispatch(deleteAllProducts());
    setDeletingAll(false);
    if (result?.ok) {
      setDeleteAllOpen(false);
      refetch();
    }
  }, [dispatch, refetch]);

  const activeFilterCount = useMemo(() => countActiveFilters(params), [params]);
  const totalCount = pagination.total ?? items.length;

  return (
    <div className="plx-page">
      <header className="plx-header">
        <div className="plx-header__title-block">
          <h1 className="plx-title">Products list</h1>
          <div className="plx-subtitle">
            {loading
              ? "Loading…"
              : `${totalCount} product${totalCount === 1 ? "" : "s"}${
                  activeFilterCount > 0 ? ` · ${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"} applied` : ""
                }`}
          </div>
        </div>
        <div className="plx-header__actions">
          <div className="plx-export-wrap">
            <ExportMenu resource="products" params={params} />
          </div>
          <button
            type="button"
            className="plx-btn-ghost"
            onClick={() => navigate("/product/import-images")}
          >
            <i className="fa fa-images" aria-hidden="true" />
            Import images
          </button>
          {canCreateLive && (
            <button
              type="button"
              className="plx-btn-primary"
              onClick={() => navigate("/addproduct")}
            >
              <i className="fa fa-plus" aria-hidden="true" />
              Create new
            </button>
          )}
          {isSuperAdmin && (
            <button
              type="button"
              className="plx-btn-danger"
              onClick={() => setDeleteAllOpen(true)}
            >
              <i className="fa fa-trash" aria-hidden="true" />
              Delete all
            </button>
          )}
        </div>
      </header>

      <div className="plx-search-row">
        <div className="plx-search">
          <i className="fa fa-search plx-search__icon" aria-hidden="true" />
          <input
            type="search"
            className="plx-search__input"
            placeholder="Search by product name or barcode"
            aria-label="Search products"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button
              type="button"
              className="plx-search__clear"
              onClick={() => setSearchInput("")}
              aria-label="Clear search"
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          )}
        </div>

        <ProductFiltersBar
          params={params}
          onChange={patchParams}
          moreOpen={moreFiltersOpen}
          onToggleMore={setMoreFiltersOpen}
          categoryOptions={filterOptions.categoryOptions}
          subcategoryOptions={filterOptions.subcategoryOptions}
          brandOptions={filterOptions.brandOptions}
          occasionOptions={filterOptions.occasionOptions}
        />
      </div>

      <main className="plx-table-wrap">
        <ProductTable
          items={items}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
          canDelete={canDelete}
          sizeById={filterOptions.sizeById}
          colorById={filterOptions.colorById}
        />
      </main>

      <footer className="plx-footer">
        <CommonPagination
          currentPage={params.page}
          totalPages={pagination.total_pages || 1}
          onPageChange={(page) => patchParams({ page })}
        />

        <div className="plx-per-page">
          <span className="plx-per-page__label">Show</span>
          <select
            aria-label="Rows per page"
            className="plx-per-page__select"
            value={params.per_page || "20"}
            onChange={(e) => patchParams({ per_page: e.target.value, page: 1 })}
          >
            {PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </footer>

      <Modal
        isOpen={Boolean(pendingDelete)}
        toggle={() => setPendingDelete(null)}
      >
        <DeleteProduct
          setModal={() => setPendingDelete(null)}
          currentComponent={pendingDelete || {}}
          deleteComponent={handleDeleteConfirm}
          itemType="product"
          modalTitle=""
        />
      </Modal>

      <Modal
        isOpen={deleteAllOpen}
        toggle={() => !deletingAll && setDeleteAllOpen(false)}
      >
        <DeleteAllProducts
          setModal={setDeleteAllOpen}
          onConfirm={handleDeleteAllConfirm}
          totalCount={totalCount}
          busy={deletingAll}
        />
      </Modal>
    </div>
  );
};

export default ProductList;
